import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { AudioButton } from '../components/AudioButton';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, BookOpen, Headphones, PenLine, Mic, Brain, Target,
  Flame, TrendingUp, TrendingDown, Minus, Loader2, Sparkles,
  RefreshCw, CheckCircle, ArrowRight, Zap, BarChart3, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_META = {
  lesen: { label: 'Lesen', icon: BookOpen, color: '#3b82f6' },
  hoeren: { label: 'Hören', icon: Headphones, color: '#8b5cf6' },
  schreiben: { label: 'Schreiben', icon: PenLine, color: '#f59e0b' },
  sprechen: { label: 'Sprechen', icon: Mic, color: '#14b8a6' },
};

const TREND_ICONS = { improving: TrendingUp, declining: TrendingDown, neutral: Minus };
const TREND_COLORS = { improving: 'text-green-500', declining: 'text-red-500', neutral: 'text-slate-400' };

export default function ExamTrainer() {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [dailyPractice, setDailyPractice] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [practiceSession, setPracticeSession] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [aRes, dRes, pRes] = await Promise.all([
        axios.get(`${API}/exam-trainer/analysis`),
        axios.get(`${API}/exam-trainer/daily-practice`),
        axios.get(`${API}/exam-trainer/progress`),
      ]);
      setAnalysis(aRes.data);
      setDailyPractice(dRes.data);
      setProgress(pRes.data);
    } catch (err) {
      toast.error('Failed to load trainer data');
    } finally {
      setLoading(false);
    }
  };

  const startTargetedPractice = async (section, level) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/exam-trainer/generate`, null, { params: { section, level, count: 5 } });
      setPracticeSession({
        section, level, questions: res.data.questions,
        originalQuestions: res.data.original_questions,
        aiGenerated: res.data.ai_generated, currentIndex: 0, answers: {},
      });
      setActiveTab('practice');
    } catch (err) {
      toast.error('Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          {[...Array(3)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-48 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="exam-trainer-page">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="trainer-back"><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2"><Brain className="h-5 w-5 text-purple-500" />Prüfungstrainer</h1>
              <p className="text-sm text-muted-foreground">AI-Powered Exam Trainer</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {progress && <Badge className="bg-amber-500"><Flame className="h-3.5 w-3.5 mr-1" />{progress.practice_streak} Tage</Badge>}
            <Link to="/exam-simulation"><Button variant="outline" size="sm"><Target className="h-4 w-4 mr-1" />Prüfungen</Button></Link>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-border bg-muted/30">
        <div className="max-w-5xl mx-auto px-4 flex gap-1">
          {[
            { id: 'overview', label: 'Überblick', icon: BarChart3 },
            { id: 'daily', label: 'Tagesübung', icon: Calendar },
            { id: 'practice', label: 'Training', icon: Zap },
            { id: 'progress', label: 'Fortschritt', icon: TrendingUp },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${activeTab === tab.id ? 'border-purple-500 text-purple-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                <Icon className="h-4 w-4" />{tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {activeTab === 'overview' && <OverviewTab analysis={analysis} onStartPractice={startTargetedPractice} generating={generating} />}
        {activeTab === 'daily' && <DailyTab dailyPractice={dailyPractice} onRefresh={fetchAll} />}
        {activeTab === 'practice' && <PracticeTab session={practiceSession} setSession={setPracticeSession} onStartPractice={startTargetedPractice} generating={generating} onComplete={fetchAll} />}
        {activeTab === 'progress' && <ProgressTab progress={progress} analysis={analysis} />}
      </main>
    </div>
  );
}

/* ==================== OVERVIEW TAB ==================== */
function OverviewTab({ analysis, onStartPractice, generating }) {
  if (!analysis) return null;
  return (
    <div className="space-y-8" data-testid="overview-tab">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-t-4 border-t-purple-500"><CardContent className="pt-5 text-center">
          <p className="text-3xl font-bold">{analysis.overall_average}%</p><p className="text-sm text-muted-foreground">Gesamt / Overall</p>
        </CardContent></Card>
        <Card className="border-t-4 border-t-blue-500"><CardContent className="pt-5 text-center">
          <p className="text-3xl font-bold">{analysis.total_exams}</p><p className="text-sm text-muted-foreground">Prüfungen</p>
        </CardContent></Card>
        <Card className="border-t-4 border-t-amber-500"><CardContent className="pt-5 text-center">
          <p className="text-3xl font-bold">{analysis.total_practice_sessions}</p><p className="text-sm text-muted-foreground">Übungen</p>
        </CardContent></Card>
        <Card className="border-t-4 border-t-teal-500"><CardContent className="pt-5 text-center">
          <p className="text-3xl font-bold">{analysis.weak_sections?.length || 0}</p><p className="text-sm text-muted-foreground">Schwächen</p>
        </CardContent></Card>
      </div>

      {/* Radar Chart */}
      <Card data-testid="radar-chart">
        <CardHeader><CardTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-purple-500" />Stärken & Schwächen / Strengths & Weaknesses</CardTitle></CardHeader>
        <CardContent>
          {analysis.radar_data?.some(d => d.overall > 0) ? (
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={analysis.radar_data}>
                <PolarGrid stroke="#334155" />
                <PolarAngleAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 13 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 11 }} />
                <Radar name="Overall" dataKey="overall" stroke="#a855f7" fill="#a855f7" fillOpacity={0.15} strokeWidth={2} />
                <Radar name="A1.1" dataKey="A1.1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} strokeWidth={1.5} />
                <Radar name="A2.1" dataKey="A2.1" stroke="#14b8a6" fill="#14b8a6" fillOpacity={0.1} strokeWidth={1.5} />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">Noch keine Daten / No data yet</p>
              <p className="text-sm">Absolvieren Sie eine Prüfung, um Ihre Stärken zu sehen.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Detail Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(SECTION_META).map(([section, meta]) => {
          const data = analysis.analysis?.[section] || {};
          const TrendIcon = TREND_ICONS[data.trend] || Minus;
          const trendColor = TREND_COLORS[data.trend] || 'text-slate-400';
          const Icon = meta.icon;
          return (
            <Card key={section} data-testid={`section-card-${section}`}>
              <CardContent className="pt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg" style={{ background: `${meta.color}15` }}><Icon className="h-5 w-5" style={{ color: meta.color }} /></div>
                    <div><p className="font-semibold">{meta.label}</p><p className="text-xs text-muted-foreground">{data.attempts || 0} Versuche</p></div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: meta.color }}>{data.average || 0}%</p>
                    <TrendIcon className={`h-4 w-4 ml-auto ${trendColor}`} />
                  </div>
                </div>
                <Progress value={data.average || 0} className="h-2 mb-3" />
                <div className="flex flex-wrap gap-1 mb-3">
                  {['A1.1', 'A1.2', 'A2.1', 'A2.2'].map(level => {
                    const ld = data.by_level?.[level];
                    return (
                      <span key={level} className={`text-xs px-2 py-0.5 rounded-full ${ld ? (ld.average >= 60 ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600') : 'bg-muted text-muted-foreground'}`}>
                        {level}: {ld ? `${ld.average}%` : '–'}
                      </span>
                    );
                  })}
                </div>
                <Button size="sm" variant="outline" className="w-full" onClick={() => onStartPractice(section, 'A1.1')} disabled={generating} data-testid={`practice-${section}`}>
                  {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />}
                  {meta.label} üben
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Weak Areas Alert */}
      {analysis.weak_sections?.length > 0 && (
        <Card className="border-l-4 border-l-amber-500 bg-amber-500/5" data-testid="weak-alert">
          <CardContent className="pt-5">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-amber-500 mt-0.5" />
              <div>
                <p className="font-semibold">KI-Empfehlung / AI Recommendation</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Ihre schwächsten Bereiche: <strong>{analysis.weak_sections.map(s => SECTION_META[s]?.label).join(', ')}</strong>.
                  Wir empfehlen täglich 10 Minuten gezieltes Training in diesen Bereichen.
                </p>
                <Button size="sm" className="mt-3 bg-amber-500 hover:bg-amber-600"
                  onClick={() => onStartPractice(analysis.weak_sections[0], 'A1.1')} disabled={generating}>
                  <Zap className="h-4 w-4 mr-1" /> Jetzt üben
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ==================== DAILY TAB ==================== */
function DailyTab({ dailyPractice, onRefresh }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const questions = dailyPractice?.questions || [];

  const submitDaily = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/exam-trainer/submit-practice`, {
        section: dailyPractice.focus_sections?.[0] || 'mixed',
        level: dailyPractice.focus_levels?.[0] || 'A1.1',
        answers,
        questions: dailyPractice.original_questions || questions,
      });
      setResult(res.data);
      setSubmitted(true);
      onRefresh();
    } catch (err) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="daily-tab">
      <Card className="border-t-4 border-t-amber-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-amber-500" />
            Tagesübung / Daily Practice
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Fokus: {dailyPractice?.focus_sections?.map(s => SECTION_META[s]?.label).join(', ')} —
            Level: {dailyPractice?.focus_levels?.join(', ')}
          </p>
        </CardHeader>
        <CardContent>
          {submitted && result ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-2xl font-bold">{result.percentage}%</p>
              <p className="text-muted-foreground">{result.correct}/{result.total} richtig — +{result.xp_earned} XP</p>
            </div>
          ) : (
            <div className="space-y-4">
              {questions.map((q, i) => (
                <div key={q.q_id} className="p-4 rounded-lg border" data-testid={`daily-q-${i}`}>
                  <p className="text-xs text-muted-foreground mb-1">Frage {i + 1} — {SECTION_META[q.type]?.label || q.type}</p>
                  {q.type === 'lesen' && (
                    <>
                      <p className="text-sm bg-muted/50 p-3 rounded mb-2">{q.text}</p>
                      <p className="font-medium mb-2">{q.question}</p>
                      <div className="grid gap-1.5">
                        {q.options?.map((opt, idx) => (
                          <button key={`${q.q_id}-opt-${idx}`} onClick={() => setAnswers(p => ({ ...p, [q.q_id]: idx }))}
                            className={`text-left p-2.5 rounded-lg border text-sm ${answers[q.q_id] === idx ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:bg-accent/30'}`}>
                            {String.fromCharCode(65 + idx)}. {opt}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {q.type === 'hoeren' && (
                    <>
                      <div className="flex items-center gap-3 bg-purple-500/5 p-3 rounded mb-2">
                        <AudioButton text={q.audio_text} voice="nova" />
                        <span className="text-sm text-purple-600">Anhören / Listen</span>
                      </div>
                      <p className="font-medium mb-2">{q.question}</p>
                      <div className="grid gap-1.5">
                        {q.options?.map((opt, idx) => (
                          <button key={`${q.q_id}-opt-${idx}`} onClick={() => setAnswers(p => ({ ...p, [q.q_id]: idx }))}
                            className={`text-left p-2.5 rounded-lg border text-sm ${answers[q.q_id] === idx ? 'border-purple-500 bg-purple-500/10' : 'border-border hover:bg-accent/30'}`}>
                            {String.fromCharCode(65 + idx)}. {opt}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {q.type === 'schreiben' && (
                    <>
                      <p className="font-medium mb-1">{q.prompt_de}</p>
                      <p className="text-sm text-muted-foreground mb-2">{q.prompt_en}</p>
                      <Textarea placeholder="Schreiben Sie hier..." rows={4} value={answers[q.q_id] || ''}
                        onChange={(e) => setAnswers(p => ({ ...p, [q.q_id]: e.target.value }))} />
                    </>
                  )}
                  {q.type === 'sprechen' && (
                    <>
                      <p className="font-medium mb-1">{q.task_de}</p>
                      <p className="text-sm text-muted-foreground mb-2">{q.task_en}</p>
                      <div className="flex items-center gap-3">
                        <MicrophoneButton expectedText={q.task_de} onPronunciationScore={(s) => setAnswers(p => ({ ...p, [q.q_id]: s.overall_score, [`${q.q_id}_score`]: s.overall_score }))} />
                        {answers[q.q_id] && <Badge className="bg-teal-500">{answers[q.q_id]}%</Badge>}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {questions.length > 0 && (
                <Button onClick={submitDaily} disabled={submitting} className="w-full bg-amber-500 hover:bg-amber-600" data-testid="submit-daily">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Abgeben / Submit
                </Button>
              )}
              {questions.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Machen Sie zuerst eine Prüfung, um personalisierte Übungen zu erhalten.</p>
                  <Link to="/exam-simulation"><Button className="mt-3" variant="outline">Zur Prüfung</Button></Link>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ==================== PRACTICE TAB ==================== */
function PracticeTab({ session, setSession, onStartPractice, generating, onComplete }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submitPractice = async () => {
    if (!session) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/exam-trainer/submit-practice`, {
        section: session.section, level: session.level,
        answers, questions: session.originalQuestions || session.questions,
      });
      setResult(res.data);
      onComplete();
    } catch (err) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-lg mx-auto text-center py-12" data-testid="practice-result">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <p className="text-4xl font-bold mb-2">{result.percentage}%</p>
        <p className="text-muted-foreground mb-4">{result.correct}/{result.total} richtig — +{result.xp_earned} XP</p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => { setResult(null); setSession(null); setAnswers({}); }}><RefreshCw className="h-4 w-4 mr-1" />Neues Training</Button>
          <Button onClick={() => { setResult(null); setAnswers({}); onStartPractice(session.section, session.level); }}>
            <Zap className="h-4 w-4 mr-1" />Nochmal
          </Button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="space-y-6" data-testid="practice-selector">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-purple-500" />Gezieltes Training starten</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">Wählen Sie einen Bereich und ein Niveau. KI generiert frische Fragen für Sie.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(SECTION_META).map(([section, meta]) => (
                <Card key={section} className="hover:shadow-md transition-all">
                  <CardContent className="pt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <meta.icon className="h-5 w-5" style={{ color: meta.color }} />
                      <p className="font-semibold">{meta.label}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['A1.1', 'A1.2', 'A2.1', 'A2.2'].map(level => (
                        <Button key={level} size="sm" variant="outline" onClick={() => onStartPractice(section, level)} disabled={generating} data-testid={`start-${section}-${level}`}>
                          {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : level}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const questions = session.questions || [];
  const sectionMeta = SECTION_META[session.section] || {};

  return (
    <div className="space-y-4" data-testid="practice-session">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge style={{ background: sectionMeta.color }}>{sectionMeta.label}</Badge>
          <Badge variant="outline">{session.level}</Badge>
          {session.aiGenerated && <Badge className="bg-purple-500"><Sparkles className="h-3 w-3 mr-1" />KI-generiert</Badge>}
        </div>
        <span className="text-sm text-muted-foreground">{Object.keys(answers).length}/{questions.length} beantwortet</span>
      </div>
      <Progress value={(Object.keys(answers).length / Math.max(questions.length, 1)) * 100} className="h-1.5" />

      {questions.map((q, i) => (
        <Card key={q.q_id} data-testid={`practice-q-${i}`}>
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground mb-2">Frage {i + 1}</p>
            {(q.type === 'lesen' || q.type === 'hoeren') && (
              <>
                {q.type === 'lesen' && <div className="bg-blue-500/5 p-3 rounded-lg mb-3 text-sm">{q.text}</div>}
                {q.type === 'hoeren' && (
                  <div className="flex items-center gap-3 bg-purple-500/5 p-3 rounded-lg mb-3">
                    <AudioButton text={q.audio_text} voice="nova" /><span className="text-sm">Anhören</span>
                  </div>
                )}
                <p className="font-medium mb-2">{q.question}</p>
                <div className="grid gap-1.5">
                  {q.options?.map((opt, idx) => (
                    <button key={`${q.q_id}-o-${idx}`} onClick={() => setAnswers(p => ({ ...p, [q.q_id]: idx }))}
                      className={`text-left p-2.5 rounded-lg border text-sm transition-all ${answers[q.q_id] === idx ? `border-2` : 'border-border hover:bg-accent/30'}`}
                      style={answers[q.q_id] === idx ? { borderColor: sectionMeta.color, background: `${sectionMeta.color}10` } : {}}>
                      {String.fromCharCode(65 + idx)}. {opt}
                    </button>
                  ))}
                </div>
              </>
            )}
            {q.type === 'schreiben' && (
              <>
                <p className="font-medium mb-1">{q.prompt_de}</p>
                <p className="text-sm text-muted-foreground mb-2">{q.prompt_en}</p>
                <Textarea rows={4} value={answers[q.q_id] || ''} placeholder="Schreiben Sie hier..."
                  onChange={(e) => setAnswers(p => ({ ...p, [q.q_id]: e.target.value }))} />
              </>
            )}
            {q.type === 'sprechen' && (
              <>
                <p className="font-medium mb-1">{q.task_de}</p>
                <p className="text-sm text-muted-foreground mb-2">{q.task_en}</p>
                <div className="flex items-center gap-3">
                  <MicrophoneButton expectedText={q.task_de}
                    onPronunciationScore={(s) => setAnswers(p => ({ ...p, [q.q_id]: s.overall_score, [`${q.q_id}_score`]: s.overall_score }))} />
                  {answers[q.q_id] != null && <Badge className="bg-teal-500">{answers[q.q_id]}%</Badge>}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ))}

      <Button onClick={submitPractice} disabled={submitting} className="w-full" data-testid="submit-practice">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
        Training abgeben / Submit Practice
      </Button>
    </div>
  );
}

/* ==================== PROGRESS TAB ==================== */
function ProgressTab({ progress, analysis }) {
  const timeline = progress?.timeline || [];

  return (
    <div className="space-y-6" data-testid="progress-tab">
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-t-4 border-t-purple-500"><CardContent className="pt-5 text-center">
          <p className="text-3xl font-bold">{progress?.total_sessions || 0}</p><p className="text-sm text-muted-foreground">Übungen</p>
        </CardContent></Card>
        <Card className="border-t-4 border-t-amber-500"><CardContent className="pt-5 text-center">
          <p className="text-3xl font-bold">{progress?.practice_streak || 0}</p><p className="text-sm text-muted-foreground">Tage Streak</p>
        </CardContent></Card>
        <Card className="border-t-4 border-t-teal-500"><CardContent className="pt-5 text-center">
          <p className="text-3xl font-bold">{analysis?.overall_average || 0}%</p><p className="text-sm text-muted-foreground">Durchschnitt</p>
        </CardContent></Card>
      </div>

      <Card data-testid="progress-chart">
        <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-teal-500" />Fortschritt über Zeit / Progress Over Time</CardTitle></CardHeader>
        <CardContent>
          {timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                <Line type="monotone" dataKey="lesen" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="Lesen" connectNulls />
                <Line type="monotone" dataKey="hoeren" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} name="Hören" connectNulls />
                <Line type="monotone" dataKey="schreiben" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} name="Schreiben" connectNulls />
                <Line type="monotone" dataKey="sprechen" stroke="#14b8a6" strokeWidth={2} dot={{ r: 4 }} name="Sprechen" connectNulls />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Noch keine Daten. Starten Sie ein Training!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
